import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type { ProfileMediaItem } from '../../services/mediaContracts';
import { toThumbnailImageUrl } from '../../services/mediaUtils';

interface MediaTileProps {
  item: ProfileMediaItem;
  /** Position within the visible list — used for first-screen eager load. */
  index?: number;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect: () => void;
  onOpen?: () => void;
  onDownload: () => void;
  downloading?: boolean;
}

const typeLabel: Record<ProfileMediaItem['type'], string> = {
  photo: 'Photo',
  video: 'Video',
  gif: 'GIF',
};

/** First-screen tiles load immediately without waiting for IntersectionObserver. */
const EAGER_TILE_COUNT = 12;
/** Start fetching media slightly before it enters the viewport. */
const LOAD_ROOT_MARGIN = '320px 0px';

const MediaTile: React.FC<MediaTileProps> = ({
  item,
  index = 0,
  selected,
  selectionMode,
  onToggleSelect,
  onOpen,
  onDownload,
  downloading,
}) => {
  // Always prefer a sized PBS thumb — never paint `name=orig` in a ~200px cell.
  const preview =
    toThumbnailImageUrl(item.thumbnailUrl || item.downloadUrl) ||
    item.thumbnailUrl ||
    item.downloadUrl;
  const tileRef = useRef<HTMLDivElement | null>(null);
  const eager = index < EAGER_TILE_COUNT;
  const [shouldLoad, setShouldLoad] = useState(eager);
  const [inViewport, setInViewport] = useState(eager);
  const [entered, setEntered] = useState(eager);

  useEffect(() => {
    const el = tileRef.current;
    if (!el) return;

    const updatePriority = (entry: IntersectionObserverEntry) => {
      const rect = entry.boundingClientRect;
      const visible =
        entry.isIntersecting &&
        rect.bottom > 0 &&
        rect.top < (window.innerHeight || document.documentElement.clientHeight);
      setInViewport(visible);
      if (entry.isIntersecting) setEntered(true);
    };

    const loadObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setShouldLoad(true);
        }
        updatePriority(entry);
      },
      { rootMargin: LOAD_ROOT_MARGIN, threshold: 0 }
    );

    const viewObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        updatePriority(entry);
      },
      { rootMargin: '0px', threshold: 0 }
    );

    loadObserver.observe(el);
    viewObserver.observe(el);
    return () => {
      loadObserver.disconnect();
      viewObserver.disconnect();
    };
  }, []);

  const handleTileClick = () => {
    if (selectionMode) {
      onToggleSelect();
    } else {
      onOpen?.();
    }
  };

  return (
    <Box
      ref={tileRef}
      onClick={handleTileClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleTileClick();
        }
      }}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        border: selected ? '2px solid #1D9BF0' : '1px solid #2F3336',
        backgroundColor: '#0F1419',
        cursor: 'pointer',
        aspectRatio: '1 / 1',
        width: '100%',
        height: '100%',
        opacity: entered ? 1 : 0,
        transform: entered ? 'none' : 'translateY(8px)',
        transition:
          'opacity 220ms ease, transform 220ms ease, border-color 160ms ease',
        '&:hover .tile-actions': {
          opacity: 1,
        },
        '&:hover .tile-open-hint': {
          opacity: selectionMode ? 0 : 1,
        },
        '&:hover .tile-media': {
          transform: 'scale(1.03)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          opacity: 1,
          transform: 'none',
          transition: 'border-color 160ms ease',
          '&:hover .tile-media': { transform: 'none' },
        },
        '&:focus-within': {
          outline: '2px solid #1D9BF0',
          outlineOffset: 2,
        },
      }}
    >
      {shouldLoad ? (
        <Box
          component="img"
          className="tile-media"
          src={preview}
          alt={item.text?.slice(0, 80) || 'Media'}
          loading={eager || inViewport ? 'eager' : 'lazy'}
          fetchPriority={inViewport ? 'high' : 'low'}
          referrerPolicy="no-referrer"
          decoding="async"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: selected ? 0.85 : 1,
            transition: 'opacity 160ms ease, transform 300ms ease',
          }}
        />
      ) : (
        <Box
          className="tile-media"
          aria-hidden
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: '#15202B',
          }}
        />
      )}

      {selected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(29, 155, 240, 0.12)',
            pointerEvents: 'none',
            transition: 'background-color 160ms ease',
          }}
        />
      )}

      {!selectionMode && (
        <Box
          className="tile-open-hint"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 160ms ease',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#E7E9EA',
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              border: '1px solid #2F3336',
            }}
          >
            {item.type === 'photo' ? 'View' : 'Play'}
          </Typography>
        </Box>
      )}

      {(item.type === 'video' || item.type === 'gif') && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlayArrowIcon sx={{ color: '#E7E9EA' }} />
          </Box>
        </Box>
      )}

      <Chip
        size="small"
        label={typeLabel[item.type]}
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          height: 22,
          fontSize: '0.7rem',
          backgroundColor: 'rgba(0,0,0,0.65)',
          color: '#E7E9EA',
          border: '1px solid #2F3336',
        }}
      />

      <Checkbox
        checked={selected}
        onChange={(e) => {
          e.stopPropagation();
          onToggleSelect();
        }}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': `Select ${typeLabel[item.type]}` }}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          color: '#E7E9EA',
          '&.Mui-checked': { color: '#1D9BF0' },
          backgroundColor: 'rgba(0,0,0,0.35)',
          borderRadius: 1,
          p: { xs: 0.75, md: 0.5 },
          zIndex: 1,
        }}
      />

      <Box
        className="tile-actions"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1,
          py: 1,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
          opacity: { xs: 1, md: 0 },
          transition: 'opacity 160ms ease',
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{ color: '#E7E9EA', flex: 1, minWidth: 0 }}
        >
          {item.text?.slice(0, 40) || `@${item.author || 'post'}`}
        </Typography>
        <IconButton
          size="small"
          aria-label="Download media"
          disabled={downloading}
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          sx={{
            color: '#E7E9EA',
            backgroundColor: downloading
              ? 'rgba(29,155,240,0.55)'
              : 'rgba(29,155,240,0.85)',
            '&:hover': { backgroundColor: '#1D9BF0' },
            width: { xs: 40, md: 36 },
            height: { xs: 40, md: 36 },
          }}
        >
          {downloading ? (
            <CircularProgress size={18} sx={{ color: '#E7E9EA' }} />
          ) : (
            <CloudDownloadIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default React.memo(MediaTile, (prev, next) =>
  prev.item === next.item &&
  prev.index === next.index &&
  prev.selected === next.selected &&
  prev.selectionMode === next.selectionMode &&
  prev.downloading === next.downloading
);
